import { useCallback, useRef } from 'react'

type AutoFocusHandler = ((event: Event) => void) | undefined

// Radix's modal Content hands focus back to its Trigger when it closes. Our
// dialogs and sheets open from ordinary state-driven buttons with no Trigger,
// so Radix found nothing to focus and keyboard users landed on <body>, losing
// their place on the page. Remember whoever had focus when the layer opened
// and go back there, unless the caller's own handler already decided.
export function useReturnFocus(onOpenAutoFocus?: AutoFocusHandler, onCloseAutoFocus?: AutoFocusHandler) {
  const opener = useRef<HTMLElement | null>(null)

  const handleOpen = useCallback(
    (event: Event) => {
      const active = document.activeElement
      opener.current = active instanceof HTMLElement && active !== document.body ? active : null
      onOpenAutoFocus?.(event)
    },
    [onOpenAutoFocus],
  )

  const handleClose = useCallback(
    (event: Event) => {
      onCloseAutoFocus?.(event)
      const target = opener.current
      opener.current = null
      if (event.defaultPrevented || !target?.isConnected) return
      event.preventDefault()
      target.focus()
    },
    [onCloseAutoFocus],
  )

  return { onOpenAutoFocus: handleOpen, onCloseAutoFocus: handleClose }
}
